import type { FlowyPlanBoard, IAgentPlan, IAgentPlanStep } from '../../models/agentMemory.model';

export type FlowyBuildingBlock = {
	id: string;
	label: string;
	board: FlowyPlanBoard;
	/** CSS variable for accent (matches board navigator / guide). */
	colourVar: string;
	summary: string;
};

/**
 * Canonical guideline building blocks Flowy may place in a session plan.
 * Labels are user-facing; summary is for the system prompt / model.
 */
export const FLOWY_BUILDING_BLOCKS: FlowyBuildingBlock[] = [
	{
		id: 'explore_problem_space',
		label: 'Get inspired/ explore',
		board: 'inspire',
		colourVar: '--orange',
		summary:
			'Open the topic with Spark, Empathise, brainstorm/common ideas, search, board review, add concepts, or PDF search. Six useful idea outputs plus at least three hearted Idea Box nodes are enough to offer moving on; keep exploring when another angle feels valuable.',
	},
	{
		id: 'research_related_work',
		label: 'Research & related work',
		board: 'inspire',
		colourVar: '--orange',
		summary:
			'Ground the session with external evidence using web search, PubMed, GDELT, and connected sources as appropriate. Three saved research outputs spanning at least two sources, lenses, or evidence categories are enough to offer moving on.',
	},
	{
		id: 'image_video_generation',
		label: 'Visual search & references',
		board: 'inspire',
		colourVar: '--orange',
		summary:
			'Use image generation, video generation, visual search from text nodes, and visual-similarity search from image nodes. The user should like at least two images before moving on; more are optional when they add a distinct visual world.',
	},
	{
		id: 'generate_taglines',
		label: 'Generate taglines',
		board: 'inspire',
		colourVar: '--orange',
		summary: 'Marketing taglines via generate / synthesise copy flows. Heart strong lines into the Idea Box as you go.',
	},
	{
		id: 'generate_haikus',
		label: 'Generate haikus',
		board: 'inspire',
		colourVar: '--orange',
		summary: 'Haiku / AI haiku style creative lines for marketing or poetry. Heart keepers into the Idea Box.',
	},
	{
		id: 'persona_stakeholder_motivations',
		label: 'Personas, stakeholders & motivations',
		board: 'ideate',
		colourVar: '--green',
		summary:
			'Create three saved outputs in total, with at least one persona and one stakeholder motivation — heart them into the Idea Box. Offer moving on once that lean mix exists; additional perspectives are optional.',
	},
	{
		id: 'define_brand_voice',
		label: 'Define brand voice',
		board: 'ideate',
		colourVar: '--green',
		summary:
			'Define a compact brand voice from one saved set of brand colours and one saved tagline. A story or character can be added optionally. Keep this step directly after Personas, stakeholders & motivations because the audience and voice inform each other.',
	},
	{
		id: 'brand_strategy',
		label: 'Develop a brand strategy',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise brand strategy narrative to narrow and materialise.',
	},
	{
		id: 'linkedin_post',
		label: 'Write a LinkedIn post',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise LinkedIn-style post for marketing.',
	},
	{
		id: 'social_media_posts',
		label: 'Create social media posts',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise social media posts grounded in the selected ideas and brand voice.',
	},
	{
		id: 'technical_spec',
		label: 'Create a technical spec',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise a concrete technical specification from the selected product context.',
	},
	{
		id: 'product_brief',
		label: 'Product brief',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise product brief.',
	},
	{
		id: 'product_idea',
		label: 'Product idea',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise product idea.',
	},
	{
		id: 'game',
		label: 'Game concept',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise game concept.',
	},
	{
		id: 'game_design_document',
		label: 'Define a game design document',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Synthesise a game design document from the strongest concepts, audience, and story context.',
	},
	{
		id: 'brainstorming_synthesise',
		label: 'Brainstorming (synthesise)',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Structured brainstorming output on Synthesise board.',
	},
	{
		id: 'story_synopsis',
		label: 'Story synopsis',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Narrative synopsis via synthesise.',
	},
	{
		id: 'pestel_swot',
		label: 'Analyse external forces (PESTEL / SWOT)',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'PESTEL and SWOT style analysis on Synthesise.',
	},
	{
		id: 'audience_profile',
		label: 'Synthesise audience profile',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Audience profile after persona work.',
	},
	{
		id: 'export_deck_pdf',
		label: 'Export slide deck / PDF',
		board: 'moodboard',
		colourVar: '--fuchsia',
		summary: 'Presentation export — usually closing step; user runs export in UI.',
	},
];

const blockById = new Map(FLOWY_BUILDING_BLOCKS.map((b) => [b.id, b]));

const PLAN_BOARDS: FlowyPlanBoard[] = ['inspire', 'ideate', 'moodboard'];
const DEFAULT_BLOCK_BY_BOARD: Record<FlowyPlanBoard, string> = {
	inspire: 'explore_problem_space',
	ideate: 'persona_stakeholder_motivations',
	moodboard: 'brainstorming_synthesise',
};

const PERSONA_BLOCK_ID = 'persona_stakeholder_motivations';
const BRAND_VOICE_BLOCK_ID = 'define_brand_voice';

const interlinkPersonaAndBrandVoiceIds = (ids: string[]): string[] => {
	const personaIndex = ids.indexOf(PERSONA_BLOCK_ID);
	const brandVoiceIndex = ids.indexOf(BRAND_VOICE_BLOCK_ID);
	if (personaIndex < 0 && brandVoiceIndex < 0) return ids;

	const withoutPair = ids.filter(
		(id) => id !== PERSONA_BLOCK_ID && id !== BRAND_VOICE_BLOCK_ID
	);
	const insertionIndex =
		personaIndex >= 0
			? ids.slice(0, personaIndex).filter(
					(id) => id !== PERSONA_BLOCK_ID && id !== BRAND_VOICE_BLOCK_ID
				).length
			: ids.slice(0, brandVoiceIndex).filter(
					(id) => id !== PERSONA_BLOCK_ID && id !== BRAND_VOICE_BLOCK_ID
				).length;
	withoutPair.splice(insertionIndex, 0, PERSONA_BLOCK_ID, BRAND_VOICE_BLOCK_ID);
	return withoutPair;
};

const interlinkPersonaAndBrandVoiceSteps = (steps: IAgentPlanStep[]): IAgentPlanStep[] => {
	const personaIndex = steps.findIndex((step) => step.buildingBlockId === PERSONA_BLOCK_ID);
	const brandVoiceIndex = steps.findIndex((step) => step.buildingBlockId === BRAND_VOICE_BLOCK_ID);
	if (personaIndex < 0 && brandVoiceIndex < 0) return steps;

	const personaStep =
		steps[personaIndex] || buildPlanStepsFromBlockIds([PERSONA_BLOCK_ID])[0];
	const brandVoiceStep =
		steps[brandVoiceIndex] || buildPlanStepsFromBlockIds([BRAND_VOICE_BLOCK_ID])[0];
	if (!personaStep || !brandVoiceStep) return steps;

	const withoutPair = steps.filter(
		(step) =>
			step.buildingBlockId !== PERSONA_BLOCK_ID &&
			step.buildingBlockId !== BRAND_VOICE_BLOCK_ID
	);
	const firstPairIndex = personaIndex >= 0 ? personaIndex : brandVoiceIndex;
	const insertionIndex = steps
		.slice(0, firstPairIndex)
		.filter(
			(step) =>
				step.buildingBlockId !== PERSONA_BLOCK_ID &&
				step.buildingBlockId !== BRAND_VOICE_BLOCK_ID
		).length;
	withoutPair.splice(insertionIndex, 0, personaStep, brandVoiceStep);
	return withoutPair;
};

export const getBuildingBlock = (id: string): FlowyBuildingBlock | undefined => blockById.get(id);

export const getBuildingBlockIds = (): string[] => FLOWY_BUILDING_BLOCKS.map((b) => b.id);

export const getBuildingBlocksCatalogForPrompt = (): string =>
	FLOWY_BUILDING_BLOCKS.map(
		(b) => `- ${b.id}: ${b.label} (${b.board} board) — ${b.summary}`
	).join('\n');

/**
 * Every session plan must contain something useful to do on every board.
 * Missing defaults are placed in board order and the plan remains within the
 * UI's ten-step limit, even when the model supplied a full ten-step plan.
 */
export const ensureBuildingBlockBoardCoverage = (
	ids: string[],
	maxSteps = 10
): string[] => {
	const limit = Math.max(PLAN_BOARDS.length + 1, maxSteps);
	let coveredIds = Array.from(
		new Set(ids.filter((id) => typeof id === 'string' && Boolean(getBuildingBlock(id))))
	);

	for (const board of PLAN_BOARDS) {
		if (coveredIds.some((id) => getBuildingBlock(id)?.board === board)) continue;
		const defaultId = DEFAULT_BLOCK_BY_BOARD[board];
		if (board === 'inspire') {
			coveredIds.unshift(defaultId);
		} else if (board === 'ideate') {
			const firstMoodboardIndex = coveredIds.findIndex(
				(id) => getBuildingBlock(id)?.board === 'moodboard'
			);
			coveredIds.splice(firstMoodboardIndex >= 0 ? firstMoodboardIndex : coveredIds.length, 0, defaultId);
		} else {
			coveredIds.push(defaultId);
		}
	}
	coveredIds = interlinkPersonaAndBrandVoiceIds(coveredIds);

	while (coveredIds.length > limit) {
		const counts = new Map<FlowyPlanBoard, number>();
		for (const id of coveredIds) {
			const board = getBuildingBlock(id)?.board;
			if (board) counts.set(board, (counts.get(board) || 0) + 1);
		}
		let removableIndex = -1;
		for (let i = coveredIds.length - 1; i >= 0; i -= 1) {
			const id = coveredIds[i];
			const board = getBuildingBlock(id)?.board;
			if (
				board &&
				(counts.get(board) || 0) > 1 &&
				id !== PERSONA_BLOCK_ID &&
				id !== BRAND_VOICE_BLOCK_ID
			) {
				removableIndex = i;
				break;
			}
		}
		if (removableIndex < 0) break;
		coveredIds.splice(removableIndex, 1);
	}

	return coveredIds;
};

export const ensureAgentPlanBoardCoverage = (
	plan: IAgentPlan,
	maxSteps = 10
): IAgentPlan => {
	const limit = Math.max(PLAN_BOARDS.length + 1, maxSteps);
	const existingSteps = (plan.steps || []).slice(0, limit);
	const coveredBoards = new Set(
		existingSteps
			.map((step) => step.board || (step.buildingBlockId ? getBuildingBlock(step.buildingBlockId)?.board : undefined))
			.filter((board): board is FlowyPlanBoard => Boolean(board))
	);
	let steps = [...existingSteps];
	for (const board of PLAN_BOARDS) {
		if (coveredBoards.has(board)) continue;
		const newStep = buildPlanStepsFromBlockIds([DEFAULT_BLOCK_BY_BOARD[board]])[0];
		if (!newStep) continue;
		newStep.id = `${newStep.buildingBlockId}-${steps.length}`;
		if (board === 'inspire') {
			steps.unshift(newStep);
		} else if (board === 'ideate') {
			const firstMoodboardIndex = steps.findIndex(
				(step) =>
					step.board === 'moodboard' ||
					(step.buildingBlockId && getBuildingBlock(step.buildingBlockId)?.board === 'moodboard')
			);
			steps.splice(firstMoodboardIndex >= 0 ? firstMoodboardIndex : steps.length, 0, newStep);
		} else {
			steps.push(newStep);
		}
		coveredBoards.add(board);
	}
	steps = interlinkPersonaAndBrandVoiceSteps(steps);

	while (steps.length > limit) {
		const counts = new Map<FlowyPlanBoard, number>();
		for (const step of steps) {
			const board =
				step.board || (step.buildingBlockId ? getBuildingBlock(step.buildingBlockId)?.board : undefined);
			if (board) counts.set(board, (counts.get(board) || 0) + 1);
		}
		let removableIndex = -1;
		for (let i = steps.length - 1; i >= 0; i -= 1) {
			const step = steps[i];
			const board =
				step.board || (step.buildingBlockId ? getBuildingBlock(step.buildingBlockId)?.board : undefined);
			if (
				board &&
				(counts.get(board) || 0) > 1 &&
				step.buildingBlockId !== PERSONA_BLOCK_ID &&
				step.buildingBlockId !== BRAND_VOICE_BLOCK_ID
			) {
				removableIndex = i;
				break;
			}
		}
		if (removableIndex < 0) break;
		steps.splice(removableIndex, 1);
	}

	return { ...plan, steps };
};

export const buildPlanStepsFromBlockIds = (ids: string[]): IAgentPlanStep[] => {
	const steps: IAgentPlanStep[] = [];
	for (let i = 0; i < ids.length; i += 1) {
		const block = getBuildingBlock(ids[i]);
		if (!block) {
			continue;
		}
		steps.push({
			id: `${block.id}-${i}`,
			label: block.label,
			status: 'pending',
			toolHint: block.id,
			buildingBlockId: block.id,
			board: block.board,
			colour: block.colourVar,
		});
	}
	return steps;
};

export const planFromProposeToolOutput = (outputJson: string): IAgentPlan | null => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(outputJson);
	} catch {
		return null;
	}
	// If the SDK wrapped a string result, unwrap one more level
	if (typeof parsed === 'string') {
		try {
			parsed = JSON.parse(parsed);
		} catch {
			return null;
		}
	}
	const obj = parsed as { plan?: IAgentPlan; buildingBlockIds?: unknown; ok?: boolean };
	if (obj.plan?.steps?.length) {
		return obj.plan;
	}
	const ids = Array.isArray(obj.buildingBlockIds)
		? obj.buildingBlockIds.filter((id): id is string => typeof id === 'string')
		: [];
	const steps = buildPlanStepsFromBlockIds(ids);
	if (steps.length) {
		return { steps };
	}
	return null;
};
